import React, { useEffect, useState, useCallback } from "react";
import { Building2, Users, Plug, ScrollText, Download, Plus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";

const ROLES = ["owner", "office_manager", "bookkeeper", "admin_staff", "cpa", "attorney"];

export default function CompanyAdmin() {
  const { active, reload, switchBusiness } = useBusiness();
  const [industries, setIndustries] = useState([]);
  const [profile, setProfile] = useState(null);
  const [members, setMembers] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [audit, setAudit] = useState([]);
  const [invite, setInvite] = useState({ email: "", role: "admin_staff" });
  const bid = active?.id;
  const canManage = active?.role === "owner" || active?.role === "office_manager";

  const loadAll = useCallback(async () => {
    if (!bid) return;
    try {
      const [p, m, i] = await Promise.all([
        api.get(`/businesses/${bid}`), api.get(`/businesses/${bid}/members`), api.get("/integrations"),
      ]);
      setProfile({ name: p.data.name, industry: p.data.industry, states: (p.data.states || []).join(", ") });
      setMembers(m.data); setIntegrations(i.data);
    } catch { /* ignore */ }
    if (canManage) { try { const { data } = await api.get(`/businesses/${bid}/audit`); setAudit(data); } catch { /* ignore */ } }
  }, [bid, canManage]);

  useEffect(() => { api.get("/industries").then(({ data }) => setIndustries(data)).catch(() => {}); }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const saveProfile = async () => {
    try {
      await api.put(`/businesses/${bid}`, { name: profile.name, industry: profile.industry, states: profile.states.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Business profile saved."); reload();
    } catch { toast.error("Could not save (owners/managers only)."); }
  };

  const createBiz = async () => {
    const name = window.prompt("New business name?");
    if (!name) return;
    try { const { data } = await api.post("/businesses", { name, industry: "Other", states: [] }); toast.success("Business created."); switchBusiness(data.id); }
    catch { toast.error("Could not create business."); }
  };

  const doInvite = async () => {
    if (!invite.email) return;
    try { await api.post(`/businesses/${bid}/members`, invite); toast.success("Invitation sent."); setInvite({ email: "", role: "admin_staff" }); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not invite."); }
  };
  const changeRole = async (m, role) => { try { await api.patch(`/businesses/${bid}/members/${m.id}`, { role }); loadAll(); } catch { toast.error("Failed."); } };
  const removeMember = async (m) => { try { await api.delete(`/businesses/${bid}/members/${m.id}`); toast.success("Removed."); loadAll(); } catch { toast.error("Failed."); } };
  const connect = async (k) => { try { await api.post(`/integrations/${k}/connect`); toast.success("Connection requested. Our team will finish setup."); loadAll(); } catch { toast.error("Failed."); } };

  const backup = () => {
    const url = `${api.defaults.baseURL}/businesses/${bid}/memory/backup`;
    api.get(`/businesses/${bid}/memory/backup`).then(({ data }) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${active.name}-business-memory.json`; a.click();
      toast.success("Business Memory backup downloaded.");
    }).catch(() => toast.error("Backup failed."));
    return url;
  };

  if (!profile) return <div className="h-40 rounded-2xl bg-white/5 animate-pulse" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-black text-2xl mb-1 flex items-center gap-2"><Building2 size={22} className="text-dox-orange" /> Company</h2>
          <p className="text-white/55 text-sm">Manage this business, your team, integrations and enterprise controls.</p>
        </div>
        <button onClick={createBiz} data-testid="create-business-btn" className="rounded-full bg-dox-orange px-4 py-2 text-sm font-bold flex items-center gap-2 hover:bg-dox-orangeHover"><Plus size={15} /> New business</button>
      </div>

      {/* Profile */}
      <Section title="Business profile" icon={Building2}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business name"><input data-testid="biz-name" className="dox-in" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></Field>
          <Field label="Industry">
            <select data-testid="biz-industry" className="dox-in" value={profile.industry} onChange={(e) => setProfile({ ...profile, industry: e.target.value })}>
              {industries.map((i) => <option key={i} value={i} className="bg-elevated">{i}</option>)}
            </select>
          </Field>
          <Field label="Operating states (comma separated)"><input data-testid="biz-states" className="dox-in" placeholder="PA, NJ, NY" value={profile.states} onChange={(e) => setProfile({ ...profile, states: e.target.value })} /></Field>
        </div>
        <button onClick={saveProfile} data-testid="save-profile" className="mt-4 rounded-full bg-dox-orange px-5 py-2.5 text-sm font-bold hover:bg-dox-orangeHover">Save profile</button>
      </Section>

      {/* Team */}
      <Section title="Team & permissions" icon={Users}>
        {canManage && (
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <input data-testid="invite-email" className="dox-in flex-1" placeholder="teammate@email.com" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            <select data-testid="invite-role" className="dox-in sm:w-48" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>
              {ROLES.filter((r) => r !== "owner").map((r) => <option key={r} value={r} className="bg-elevated">{label(r)}</option>)}
            </select>
            <button onClick={doInvite} data-testid="invite-btn" className="rounded-xl bg-dox-orange px-5 text-sm font-bold hover:bg-dox-orangeHover">Invite</button>
          </div>
        )}
        <div className="space-y-2">
          {members.map((m) => (
            <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-dox-orange to-dox-purple flex items-center justify-center text-xs font-bold shrink-0">{(m.name || m.email).slice(0, 1).toUpperCase()}</div>
              <div className="min-w-0 flex-1"><div className="text-sm text-white truncate">{m.name || m.email}</div><div className="text-xs text-white/40">{m.email} · {m.status}</div></div>
              {canManage && m.role !== "owner" ? (
                <>
                  <select value={m.role} onChange={(e) => changeRole(m, e.target.value)} className="dox-in !py-1 !w-36 text-xs">
                    {ROLES.filter((r) => r !== "owner").map((r) => <option key={r} value={r} className="bg-elevated">{label(r)}</option>)}
                  </select>
                  <button onClick={() => removeMember(m)} className="text-red-400/70 hover:text-red-400"><Trash2 size={15} /></button>
                </>
              ) : <span className="text-xs font-bold text-dox-orange">{label(m.role)}</span>}
            </div>
          ))}
        </div>
      </Section>

      {/* Integrations */}
      <Section title="Integrations" icon={Plug}>
        <div className="grid sm:grid-cols-3 gap-3">
          {integrations.map((i) => (
            <div key={i.key} data-testid={`integration-${i.key}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-center justify-between">
              <span className="text-sm text-white">{i.name}</span>
              {i.status === "not_connected" ? (
                <button onClick={() => connect(i.key)} className="text-xs font-bold text-dox-orange hover:underline">Connect</button>
              ) : <span className="text-xs font-bold text-amber-400 capitalize">{i.status}</span>}
            </div>
          ))}
        </div>
        <p className="text-xs text-white/35 mt-3">Requesting a connection notifies our team to complete secure setup for your account.</p>
      </Section>

      {/* Enterprise */}
      <Section title="Enterprise & data" icon={ShieldCheck}>
        <div className="flex flex-wrap gap-3">
          <button onClick={backup} data-testid="memory-backup" className="rounded-full glass px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-white/10"><Download size={15} /> Back up Business Memory</button>
        </div>
        <p className="text-xs text-white/40 mt-2">Documents are stored in encrypted object storage. Business Memory backups let you export everything Dox knows at any time.</p>
        {canManage && (
          <div className="mt-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/50 font-semibold mb-2"><ScrollText size={13} /> Audit log</div>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {audit.length === 0 ? <p className="text-white/35 text-sm">No activity logged yet.</p> :
                audit.map((a) => (
                  <div key={a.id} className="text-xs text-white/60 flex items-center justify-between border-b border-white/5 pb-1.5">
                    <span><span className="text-white/80 font-mono">{a.action}</span> · {a.actor_name}</span>
                    <span className="text-white/30">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </Section>

      <style>{`.dox-in{background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.1);border-radius:12px;padding:10px 12px;color:#fff;font-size:14px;outline:none;width:100%;}.dox-in:focus{border-color:#1E78FF;}`}</style>
    </div>
  );
}

function label(r) { return r.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()); }
function Field({ label, children }) { return <label className="block"><span className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">{label}</span>{children}</label>; }
function Section({ title, icon: Icon, children }) {
  return (
    <div className="glass-card p-6">
      <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Icon size={18} className="text-dox-orange" /> {title}</h3>
      {children}
    </div>
  );
}
